import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

type ToastKind = 'info' | 'error' | 'success';
/** Optionaler Link im Toast (z. B. #123: „Zum Projekt P-xxxx" nach dem Wiederbeleben). */
type ToastLink = { to: string; label: string };
type Toast = {
  id: number;
  kind: ToastKind;
  title: string;
  body: string | undefined;
  link: ToastLink | undefined;
};

type ToastInput = {
  kind?: ToastKind;
  title: string;
  body?: string | undefined;
  link?: ToastLink | undefined;
};

type ToastApi = {
  push: (t: ToastInput) => void;
  error: (title: string, body?: string | undefined) => void;
  success: (title: string, body?: string | undefined) => void;
};

const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);

  const push = useCallback<ToastApi['push']>((t) => {
    const id = Date.now() + Math.random();
    const next: Toast = {
      id,
      kind: t.kind ?? 'info',
      title: t.title,
      body: t.body,
      link: t.link,
    };
    setItems((prev) => [...prev, next]);
    // #123 V1: Ein Toast mit Link bleibt stehen, bis er geschlossen wird — wer per Tastatur oder
    // Screenreader zum Link will, braucht dafür länger als 6 s (WCAG 2.2.1). Ohne Link genügt
    // die kurze Anzeige.
    if (!next.link) {
      setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== id)), 6000);
    }
  }, []);

  const api: ToastApi = {
    push,
    error: (title, body) => push({ kind: 'error', title, body }),
    success: (title, body) => push({ kind: 'success', title, body }),
  };

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
        {items.map((t) => (
          <ToastItem
            key={t.id}
            toast={t}
            onClose={() => setItems((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);
  const tone =
    toast.kind === 'error'
      ? 'border-red-300 bg-red-50 text-red-900'
      : toast.kind === 'success'
        ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
        : 'border-ink-300 bg-white text-ink-900';
  return (
    <div
      // Mit Link: `status` — ein `alert` mit interaktivem Inhalt unterbricht den Screenreader und
      // lässt den Link nicht in Ruhe erreichen. Fehler/Meldungen ohne Link bleiben `alert`.
      role={toast.link ? 'status' : 'alert'}
      className={`rounded-lg border shadow-lift px-4 py-3 transition-all ${tone} ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">{toast.title}</div>
          {toast.body && (
            <div className="text-xs mt-1 whitespace-pre-wrap break-words">{toast.body}</div>
          )}
          {toast.link && (
            <Link
              to={toast.link.to}
              onClick={onClose}
              className="text-xs mt-1 inline-block underline"
            >
              {toast.link.label}
            </Link>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs opacity-60 hover:opacity-100 px-1"
          aria-label={t('v2.common.close', 'Schließen')}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
